import { Box, Group, NavLink, ScrollArea, Stack, Text } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConsolePrefix } from './nav';
import { tokens as T } from '../theme/tokens';
import { useEstatesStore, useTenantsStore } from '../stores/dataStores';
import { rowsOf } from '../stores/collection';

/* The sidebar.
 *
 * The estate list under "Estates" is data, not routing config — it is however
 * many estates exist, with however many tenants each. NavLink carries both the
 * nesting and the right-aligned count, so this is configuration rather than a
 * bespoke tree. */

const TILE: Record<string, string> = {
  overview: T.marigold,
  pool: T.coral,
  ledger: T.teal,
  estates: T.periwinkle,
  memory: T.violet,
};

function Tile({ tone, shape }: { tone: string; shape: 'dot' | 'square' }) {
  return (
    <Box
      style={{
        width: 26,
        height: 26,
        flex: 'none',
        borderRadius: 8,
        background: tone,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Box
        style={{
          width: 9,
          height: 9,
          background: '#fff',
          borderRadius: shape === 'dot' ? '50%' : 2,
        }}
      />
    </Box>
  );
}

function Caption({ children, mt }: { children: string; mt?: number }) {
  return (
    <Text
      fz={10}
      fw={700}
      c={T.ink5}
      mt={mt}
      px={8}
      pt={6}
      pb={4}
      style={{ letterSpacing: '0.09em' }}
    >
      {children}
    </Text>
  );
}

function Item({
  label,
  to,
  tone,
  shape = 'square',
  active,
}: {
  label: string;
  to: string;
  tone: string;
  shape?: 'dot' | 'square';
  active: boolean;
}) {
  const navigate = useNavigate();
  return (
    <NavLink
      label={label}
      active={active}
      onClick={() => navigate(to)}
      leftSection={<Tile tone={tone} shape={shape} />}
      styles={{
        root: {
          borderRadius: 9,
          padding: 8,
          '--nl-bg': T.navActive,
          '--nl-color': T.ink,
          '--nl-hover': T.navActive,
        },
        label: { fontSize: 14, fontWeight: 600 },
      }}
    />
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const estates = useEstatesStore((s) => s.data);
  const tenants = useTenantsStore((s) => s.data);
  const estateList = rowsOf(estates);
  const tenantRows = rowsOf(tenants);
  const devPrefix = useConsolePrefix();
  const at = (p: string) =>
    p === '/' ? pathname === devPrefix || pathname === `${devPrefix}/` : pathname.startsWith(`${devPrefix}${p}`);

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
      }}
    >
      <Group gap={10} px={14} pt={16} pb={18} wrap="nowrap">
        <Box style={{ width: 26, height: 26, borderRadius: 999, background: T.coral, flex: 'none' }} />
        <Box style={{ lineHeight: 1.2 }}>
          <Text fz={14} fw={800}>
            Bit Haus
          </Text>
          <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }}>
            CONTROL PLANE
          </Text>
        </Box>
      </Group>

      <ScrollArea style={{ flex: 1 }} px={14} pb={24}>
        <Item label="Device grants" to={`${devPrefix}/memberships`} tone={T.teal} active={at('/memberships')} />
          <Caption>OPERATIONS</Caption>
        <Stack gap={2}>
          <Item label="Router overview" to={`${devPrefix}/`} tone={TILE.overview!} shape="dot" active={at('/')} />
          <Item label="Credential pool" to={`${devPrefix}/pool`} tone={TILE.pool!} active={at('/pool')} />
          <Item label="Usage ledger" to={`${devPrefix}/ledger`} tone={TILE.ledger!} active={at('/ledger')} />
          <Item label="Agent memory" to={`${devPrefix}/memory`} tone={TILE.memory!} active={at('/memory')} />
        </Stack>

        <Caption mt={8}>TENANCY</Caption>
        <Stack gap={2}>
          <Item label="Estates" to={`${devPrefix}/estates`} tone={TILE.estates!} active={at('/estates')} />
          {estateList.map((e) => {
            const active = pathname.startsWith(`${devPrefix}/estates/${e.id}`);
            return (
              <Group
                key={e.id}
                justify="space-between"
                wrap="nowrap"
                gap={10}
                onClick={() => navigate(`${devPrefix}/estates/${e.id}`)}
                style={{
                  padding: '6px 8px 6px 45px',
                  borderRadius: 9,
                  cursor: 'pointer',
                  background: active ? T.navActive : 'transparent',
                }}
              >
                <Text fz={13} fw={600} c={active ? T.ink : T.ink3} truncate>
                  {e.displayName}
                </Text>
                <Text fz={11} c={T.ink5} style={{ flex: 'none' }}>
                  {tenantRows.filter((t) => t.estateId === e.id).length || ''}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </ScrollArea>
    </Box>
  );
}
